import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getUserLevel } from '../utils/LevelSystem';
import { STokenLogo } from '../components/StreamDisplay';
import './Leaderboards.css';

const TABS = [
  { id: 'streams',  label: 'Streams',  field: 'totalStreams' },
  { id: 'level',    label: 'Level',    field: 'totalXp' },
  { id: 'activity', label: 'Active',   field: 'interactions' },
];

const RANK_ICONS = { 1: '🥇', 2: '🥈', 3: '🥉' };

function getStatus(rank) {
  if (rank === 1)          return { label: 'CHAMPION', color: '#ffd700' };
  if (rank <= 3)           return { label: 'ELITE',    color: '#ffaa00' };
  if (rank <= 10)          return { label: 'PRO',      color: '#3498db' };
  if (rank <= 25)          return { label: 'VETERAN',  color: '#FF8C42' };
  return                          { label: 'AGENT',    color: '#95a5a6' };
}

function SkeletonRow() {
  return (
    <div className="leaderboards__row leaderboards__row--skeleton">
      <div className="skeleton leaderboards__skel-rank" />
      <div className="skeleton leaderboards__skel-avatar" />
      <div className="leaderboards__skel-info">
        <div className="skeleton leaderboards__skel-name" />
        <div className="skeleton leaderboards__skel-sub" />
      </div>
      <div className="skeleton leaderboards__skel-status" />
      <div className="skeleton leaderboards__skel-value" />
    </div>
  );
}

export default function Leaderboards() {
  const [activeTab, setActiveTab] = useState('streams');
  const [entries, setEntries]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setError(false);
    setEntries([]);

    const { field } = TABS.find(t => t.id === activeTab);
    const q = query(collection(db, 'users'), orderBy(field, 'desc'), limit(50));

    getDocs(q)
      .then(snap => {
        setEntries(snap.docs.map((d, i) => ({ ...d.data(), _rank: i + 1 })));
      })
      .catch(err => {
        console.error('Leaderboard fetch failed:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [activeTab]);

  return (
    <div className="page leaderboards">
      <div className="grid-bg" aria-hidden="true" />

      <header className="leaderboards__header">
        <button className="page-back-btn" onClick={() => navigate('/')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Home
        </button>
        <h1 className="leaderboards__title">Leaderboard</h1>
        <p className="leaderboards__subtitle">Top 50 agents worldwide</p>
      </header>

      {/* Tabs */}
      <div className="leaderboards__tabs" role="tablist">
        {TABS.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`leaderboards__tab${activeTab === tab.id ? ' leaderboards__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Column headers */}
      <div className="leaderboards__col-headers">
        <span className="leaderboards__col-rank">#</span>
        <span className="leaderboards__col-user">User</span>
        <span className="leaderboards__col-status">Status</span>
        <span className="leaderboards__col-val">
          {activeTab === 'streams' ? 'Streams' : activeTab === 'level' ? 'Level' : 'Activity'}
        </span>
      </div>

      {/* List */}
      <div className="leaderboards__list" role="tabpanel">
        {loading && Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}

        {!loading && error && (
          <div className="leaderboards__empty">
            <span>⚠️</span>
            <p>Failed to load leaderboard. Try refreshing.</p>
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="leaderboards__empty">
            <span>📊</span>
            <p>No agents ranked yet. Be the first.</p>
          </div>
        )}

        {!loading && !error && entries.map(entry => {
          const status = getStatus(entry._rank);
          const level  = getUserLevel(entry.totalXp || 0);
          return (
            <div
              key={entry._rank}
              className={`leaderboards__row${entry._rank <= 3 ? ' leaderboards__row--podium' : ''}`}
            >
              {/* Rank */}
              <div className="leaderboards__rank">
                {RANK_ICONS[entry._rank]
                  ? <span className="leaderboards__rank-icon">{RANK_ICONS[entry._rank]}</span>
                  : <span className="leaderboards__rank-num">{entry._rank}</span>
                }
              </div>

              {/* Avatar */}
              <div className={`leaderboards__avatar${entry.activeBorder ? ` leaderboards__avatar--border-${entry.activeBorder}` : ''}`}>
                {entry.activeBorder
                  ? <span className="leaderboards__avatar-inner">{(entry.username || entry.displayName || entry.email || 'A')[0].toUpperCase()}</span>
                  : (entry.username || entry.displayName || entry.email || 'A')[0].toUpperCase()
                }
              </div>

              {/* Info */}
              <div className="leaderboards__info">
                <div className="leaderboards__name">
                  {entry.username || entry.displayName || 'Agent'}
                </div>
                <div className="leaderboards__sub">Lv. {level}</div>
              </div>

              {/* Status badge */}
              <div className="leaderboards__status" style={{ '--status-color': status.color }}>
                {status.label}
              </div>

              {/* Value */}
              <div className="leaderboards__value">
                {activeTab === 'streams' && (
                  <div className="leaderboards__streams">
                    <STokenLogo size={14} />
                    <span>{(entry.totalStreams || 0).toLocaleString()}</span>
                  </div>
                )}
                {activeTab === 'level' && (
                  <span className="leaderboards__score">{level}</span>
                )}
                {activeTab === 'activity' && (
                  <span className="leaderboards__score">{(entry.interactions || 0).toLocaleString()}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
